// Sestavení a stažení komplexního PDF reportu.
// Frontend poskládá obsah (výsledky nástrojů + doporučení + postup opravy ze
// znalostní báze) a pošle ho backendu (POST /tools/report-pdf), který vyrenderuje
// brandované PDF. PDF je prémiová funkce (plán Starter+).

import api from './api'
import { getKbEntry } from '../data/knowledgeBase'

export type FindingStatus = 'pass' | 'warning' | 'fail'

export interface Finding {
  label?: string
  status?: FindingStatus
  message?: string
  recommendation?: string
}

export interface ReportSection {
  name: string
  score?: number | null
  findings: Finding[]
  howTo?: { what?: string; steps?: string[]; conclusion?: string }
}

export interface ReportPayload {
  title: string
  url: string
  domain: string
  score?: number | null
  sections: ReportSection[]
}

/** Je plán placený (Starter+)? PDF je dostupné jen od Starter. */
export function isPaidPlan(plan?: string | null): boolean {
  return !!plan && plan !== 'free'
}

const normStatus = (v: any): FindingStatus => {
  if (v === 'pass' || v === true) return 'pass'
  if (v === 'fail' || v === 'error' || v === false) return 'fail'
  return 'warning'
}

/** Generický extraktor zjištění z různých tvarů výsledků nástrojů. */
export function extractFindings(result: any): Finding[] {
  if (!result || typeof result !== 'object') return []
  const out: Finding[] = []

  // a) checks: { key: { pass|status, label, message, recommendation } }
  if (result.checks && typeof result.checks === 'object') {
    for (const [key, v] of Object.entries<any>(result.checks)) {
      const status = v?.pass !== undefined ? normStatus(v.pass) : normStatus(v?.status)
      out.push({
        label: v?.label || key,
        status,
        message: v?.message,
        recommendation: status !== 'pass' ? v?.recommendation : undefined,
      })
    }
  }

  // b) opportunities (pagespeed) / diagnostics
  for (const arrKey of ['opportunities', 'diagnostics']) {
    if (Array.isArray(result[arrKey])) {
      for (const o of result[arrKey]) {
        out.push({
          label: o?.title,
          status: 'warning',
          message: o?.description || o?.display_value,
        })
      }
    }
  }

  // c) recommendations: string[] | {title,message,recommendation,type}[]
  if (Array.isArray(result.recommendations)) {
    for (const r of result.recommendations) {
      if (typeof r === 'string') out.push({ status: 'warning', message: r })
      else if (r && typeof r === 'object') {
        out.push({
          label: r.title || r.label,
          status: normStatus(r.type || r.status),
          message: r.message || r.description,
          recommendation: r.recommendation,
        })
      }
    }
  }

  // d) issues: {type,message}[]
  if (Array.isArray(result.issues)) {
    for (const i of result.issues) {
      if (i && typeof i === 'object') out.push({ status: normStatus(i.type), message: i.message })
    }
  }

  // e) technologies: [{category, name}] — reálně detekovaný stack (nástroj Technologie)
  if (Array.isArray(result.technologies)) {
    if (result.technologies.length === 0) {
      out.push({ status: 'warning', message: 'Nepodařilo se spolehlivě detekovat použité technologie (web je může skrývat).' })
    } else {
      for (const tch of result.technologies) {
        if (tch && typeof tch === 'object') {
          out.push({ label: tch.category, status: 'pass', message: tch.name })
        }
      }
    }
  }

  return out
}

/** Postup opravy ze znalostní báze pro dané téma (kbId). */
export function howToFromKb(kbId?: string): ReportSection['howTo'] | undefined {
  if (!kbId) return undefined
  const e = getKbEntry(kbId)
  if (!e) return undefined
  return { what: e.what, steps: e.how, conclusion: e.conclusion }
}

/**
 * Sestaví sekce reportu z výsledků SEO analýzy (18 kontrol). Každá kontrola =
 * sekce s nálezem, doporučením (i18n check_details) a postupem opravy z KB.
 */
export function buildAnalysisSections(
  results: Record<string, any>,
  t: (k: string, o?: any) => string,
): ReportSection[] {
  const entries = Object.entries(results || {})
  const ordered = [
    ...entries.filter(([, v]: [string, any]) => v?.status !== 'pass'),
    ...entries.filter(([, v]: [string, any]) => v?.status === 'pass'),
  ]
  return ordered.map(([key, v]: [string, any]) => {
    const isPass = v?.status === 'pass'
    const rec = isPass ? '' : t(`check_details.${key}_rec`, { defaultValue: '' })
    return {
      name: t(`checks.${key}`, { defaultValue: key }),
      score: typeof v?.score === 'number' ? v.score : null,
      findings: [{
        status: isPass ? 'pass' : (v?.status === 'fail' ? 'fail' : 'warning') as FindingStatus,
        message: v?.message,
        recommendation: rec || undefined,
      }],
      howTo: isPass ? undefined : howToFromKb(key),
    }
  })
}

/** Datum pro název souboru, formát YYYY-MM-DD. */
export function dateStamp(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Bezpečný název souboru: {label}-{doména}-{datum}.pdf */
export function reportFileName(label: string, domain?: string): string {
  const dom = (domain || 'web').replace(/[^a-z0-9.-]/gi, '_')
  return `${label}-${dom}-${dateStamp()}.pdf`
}

/**
 * Stáhne komplexní PDF report (POST blob). Backend ověřuje plán (Starter+).
 * `fileLabel` rozlišuje typ: 'analyza' (jedna stránka) vs 'tools-analyza' (Spustit vše).
 */
export async function downloadComprehensiveReport(payload: ReportPayload, fileLabel = 'analyza'): Promise<void> {
  const res = await api.post('/tools/report-pdf', payload, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = reportFileName(fileLabel, payload.domain)
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(blobUrl)
}
